import type { AircraftType, Pad, Runway } from './types.ts';
import { aircraftType } from './aircraft.ts';

/**
 * Level layouts are described in "L units": (0,0) is the layout centre
 * (cx = W/2, cy = H*0.55) and 1 unit = L = min(W,H)*0.42 of the logical
 * viewport. That keeps a layout playable on iPhone portrait, iPad portrait
 * and iPad landscape. `frac` positions are in screen fractions instead.
 */
export interface RunwaySpec { id?: string; x1: number; y1: number; x2: number; y2: number; w?: number }
export interface PadSpec { id?: string; x: number; y: number; r?: number; frac?: boolean }

export interface Palette {
  bg: string; field: string; deep: string;
  water?: string; ink?: string;
}

// ---------- the city's sound ----------
// The sound layer (src/app/sound.ts, concept in docs/research/music.md) reads these; the sim
// never does. They sit on the LevelDef the way `palette` and `decor` do: a city's identity is
// its key, its tempo and its room as much as its runways. The knob names are the tuning study's
// own (docs/prototype/quiet-skies-sound-v1.html), so a settings line copied out of it pastes in.
export type ScaleName =
  | 'major pentatonic' | 'minor pentatonic' | 'suspended' | 'yo' | 'kumoi' | 'lydian pentatonic'
  | 'insen (semitone)' | 'hirajoshi (semitone)';
export type LandingNote = 'tonic + fifth' | 'tonic' | 'fifth' | 'chord tone';
export type DroneRegister = 'root, deep' | 'root, low' | 'fifth, deep' | 'fifth, low';
export type DroneMotion = 'stays on the root' | 'follows the chord';
export type PadVoice = 'warm' | 'strings' | 'glass' | 'organ' | 'breath';
export type PadVoicing = 'close' | 'open' | 'low' | 'high';
export type ChordCycle = 'i vi ii v' | 'i v' | 'rising' | 'one chord';
export type Journey = 'off' | 'gentle' | 'full';
export type Drift = 'none' | 'relative' | 'fifths';
export type DriftEvery = 'never' | '3' | '2' | '1';

/** Every knob the sound layer has, fully resolved (src/app/music.ts supplies the base). */
export interface MusicConfig {
  // key
  /** the tonic as a MIDI note: the threshold ring, where every line resolves */
  root: number;
  /** the five notes the sky may use; the semitone modes are darker */
  mode: ScaleName;
  /** the grid every struck note snaps to */
  bpm: number;
  /** bars between the pad's chord changes */
  chordBars: number;
  // the field
  /** px per scale degree; tighter is more notes per approach */
  ring: number;
  /** px an aircraft flies between notes; smaller is busier */
  tick: number;
  /** bars between an unrouted aircraft's questions */
  calls: number;
  /** which note an arrival comes home to */
  landing: LandingNote;
  // the drone
  /** how loud the ground hum is */
  drone: number;
  /** root or a fifth below it; the fifth grounds without spelling the tonic */
  droneReg: DroneRegister;
  /** stay put, or move with the pad chord's bass */
  droneMove: DroneMotion;
  /** a slow breath in the drone, about half a minute long (0 = steady) */
  droneSwell: number;
  /** the drone fades out for this last fraction of each chord and returns with the next */
  droneRest: number;
  /** the drone's lowpass in Hz; lower is felt more than heard */
  droneCut: number;
  /** detune between the drone's two sines in cents; zero is a still tone */
  droneShimmer: number;
  // the pad
  /** the slow chord under everything */
  pad: number;
  /** warm triangles, bowed strings, glass partials, organ drawbars, or a breath of filtered noise */
  padType: PadVoice;
  /** where the three chord tones sit: close under the field, spread open, low, or up in the field */
  padVoicing: PadVoicing;
  /** the cycle the pad walks through, one step per `chordBars` */
  padProg: ChordCycle;
  /** how far the pad's filter opens and closes over a minute */
  padMove: number;
  /** seconds the pad takes to slide to the next chord */
  padGlide: number;
  // the bed
  /** pink noise that opens with traffic */
  wind: number;
  /** a resting heartbeat on the beat; zero removes it */
  pulse: number;
  /** chance per beat of a high note somewhere in the sky */
  sparks: number;
  // space
  /** how much of everything goes to the room */
  verb: number;
  /** how long the room rings, seconds */
  space: number;
  /** lowpass on the aircraft voices in Hz; lower is softer */
  bright: number;
  // the hour: the conductor's slow arc over tens of minutes
  /** off leaves the bed as tuned; gentle and full walk the arc, full also picks the pad's register, voicing and cycle */
  journey: Journey;
  /** seconds a phase lasts before the next (jittered, sometimes skipped) */
  phaseLen: number;
  /** how the key wanders from the tuned root and back, at a chord change */
  drift: Drift;
  /** phases between a drift */
  driftEvery: DriftEvery;
  /** presence of the slow arpeggio the day and high phases add; 0 keeps it off */
  motif: number;
}

/** A city's sound: whatever differs from the base. `root`, `mode`, `bpm`, `wind`, `verb` and
 *  `bright` are what really make a city; anything else is a knob turned by ear for it. */
export type MusicDef = Partial<MusicConfig>;

export interface LevelDef {
  id: string;
  city: string;
  country: string;
  /** one-line flavour shown on the level card */
  blurb: string;
  /** points required to clear the city and unlock the next one. Play does not
   *  stop there — a cleared level can be flown on for score (see `flyOn`). */
  baseline: number;
  /** difficulty multiplier on every arrival's points: a busier field pays
   *  better for the same aircraft (see `landingPoints`) */
  scoreMul: number;
  /** airborne cap = min(capMax, capBase + floor(arrivals / capDiv)) */
  capBase: number;
  capDiv: number;
  capMax: number;
  /** a newly scheduled arrival is announced this many seconds ahead: [min,max] */
  spawnLead: [number, number];
  /** minimum seconds between two spawns */
  minSpawnGap: number;
  /** global speed multiplier for this level (tempo) */
  tempo: number;
  /** aircraft type mix: [typeId, weight] */
  mix: [string, number][];
  layout: { runways: RunwaySpec[]; pads: PadSpec[] };
  palette: Palette;
  /** decorative motif ids interpreted by the renderer */
  decor: string[];
  /** optional water polygon in screen fractions, closed automatically */
  water?: { x: number; y: number }[];
  /** which side(s) arrivals prefer, weights for [top,right,bottom,left]; default even */
  arrivalSides?: [number, number, number, number];
  /** the city's key, tempo and room (see `MusicDef`); cosmetic like `palette`, the sim never reads it */
  music?: MusicDef;
}

export interface Layout { runways: Runway[]; pads: Pad[]; cx: number; cy: number; L: number }

/**
 * What one arrival scores: the aircraft type's own value (how much attention
 * it costs — see `AIRCRAFT`) times the city's difficulty. Both halves matter:
 * a jet is always worth four lights, and the same jet is worth more over
 * amsterdam than over prague. Rounded, so a score is always whole numbers and
 * the value of a given type in a given city never changes.
 */
export function landingPoints(type: AircraftType, level: LevelDef): number {
  return Math.round(type.points * level.scoreMul);
}

/** Mean points an arrival is worth on this level, given its mix — what the
 *  baselines are set from, and how many arrivals a baseline really asks for. */
export function expectedPointsPerArrival(level: LevelDef): number {
  return level.mix.reduce((sum, [id, weight]) => sum + weight * landingPoints(aircraftType(id), level), 0);
}

export function layoutLevel(def: LevelDef, W: number, H: number): Layout {
  const cx = W / 2, cy = H * 0.55;
  const L = Math.min(W, H) * 0.42;
  const runways: Runway[] = def.layout.runways.map((r, i) =>
    mkRunway(r.id ?? `${def.id}-rw${i}`, cx + r.x1 * L, cy + r.y1 * L, cx + r.x2 * L, cy + r.y2 * L, r.w ?? 26));
  const pads: Pad[] = def.layout.pads.map((p, i) => ({
    id: p.id ?? `${def.id}-pad${i}`,
    x: p.frac ? Math.min(W * p.x, W - 56) : cx + p.x * L,
    y: p.frac ? H * p.y : cy + p.y * L,
    r: p.r ?? 15,
  }));
  return { runways, pads, cx, cy, L };
}

export function mkRunway(id: string, x1: number, y1: number, x2: number, y2: number, w: number): Runway {
  const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy);
  return { id, x1, y1, x2, y2, w, len, ang: Math.atan2(dy, dx) };
}
